import articlesAPI from 'dashboard/api/helpCenter/articles';
import { uploadExternalImage, uploadFile } from 'dashboard/helper/uploadHelper';
import { throwErrorMessage } from 'dashboard/store/utils/api';

import types from '../../mutation-types';

const latestStatusSequenceByArticle = {};
const latestContentSequenceByArticle = {};

const mergeArticleUpdate = ({ current, payload, isStatusUpdate }) => {
  if (!current) return payload;

  if (isStatusUpdate) {
    return {
      ...current,
      status: payload.status,
      preview_token: payload.preview_token,
    };
  }

  const keepsSameStatus = payload.status === current.status;

  return {
    ...payload,
    status: current.status,
    preview_token: keepsSameStatus
      ? payload.preview_token
      : current.preview_token,
  };
};

export const actions = {
  index: async (
    { commit },
    { pageNumber, portalSlug, locale, status, authorId, categorySlug }
  ) => {
    try {
      commit(types.SET_UI_FLAG, { isFetching: true });
      const {
        data: { payload, meta },
      } = await articlesAPI.getArticles({
        pageNumber,
        portalSlug,
        locale,
        status,
        authorId,
        categorySlug,
      });
      const articleIds = payload.map(article => article.id);
      commit(types.CLEAR_ARTICLES);
      commit(types.ADD_MANY_ARTICLES, payload);
      commit(types.SET_ARTICLES_META, meta);
      commit(types.ADD_MANY_ARTICLES_ID, articleIds);
      return articleIds;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit, dispatch }, { portalSlug, ...articleObj }) => {
    commit(types.SET_UI_FLAG, { isCreating: true });
    try {
      const {
        data: { payload },
      } = await articlesAPI.createArticle({
        portalSlug,
        articleObj,
      });
      const { id: articleId } = payload;
      commit(types.ADD_ARTICLE, payload);
      commit(types.ADD_ARTICLE_ID, articleId);
      commit(types.ADD_ARTICLE_FLAG, articleId);
      dispatch('portals/updatePortal', portalSlug, { root: true });
      return articleId;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_UI_FLAG, { isCreating: false });
    }
  },

  show: async ({ commit, getters: storeGetters }, { id, portalSlug }) => {
    const statusSequenceAtRequest = latestStatusSequenceByArticle[id] || 0;

    commit(types.SET_UI_FLAG, { isFetching: true });
    try {
      const response = await articlesAPI.getArticle({ id, portalSlug });
      const {
        data: { payload },
      } = response;
      const { id: articleId } = payload;
      const hasNewerStatus =
        (latestStatusSequenceByArticle[id] || 0) !== statusSequenceAtRequest;

      commit(
        types.ADD_ARTICLE,
        hasNewerStatus
          ? mergeArticleUpdate({
              current: storeGetters.articleById(articleId),
              payload,
              isStatusUpdate: false,
            })
          : payload
      );
      commit(types.ADD_ARTICLE_ID, articleId);
      commit(types.SET_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_UI_FLAG, { isFetching: false });
    }
  },

  update: async (
    { commit, getters: storeGetters },
    { portalSlug, articleId, ...articleObj }
  ) => {
    const isStatusUpdate = articleObj.status !== undefined;
    const sequences = isStatusUpdate
      ? latestStatusSequenceByArticle
      : latestContentSequenceByArticle;
    const sequence = (sequences[articleId] || 0) + 1;
    sequences[articleId] = sequence;

    commit(types.UPDATE_ARTICLE_FLAG, {
      uiFlags: isStatusUpdate
        ? { isUpdating: true, isUpdatingStatus: true }
        : { isUpdating: true },
      articleId,
    });

    try {
      const {
        data: { payload },
      } = await articlesAPI.updateArticle({
        portalSlug,
        articleId,
        articleObj,
      });

      if (sequences[articleId] === sequence) {
        commit(
          types.UPDATE_ARTICLE,
          mergeArticleUpdate({
            current: storeGetters.articleById(articleId),
            payload,
            isStatusUpdate,
          })
        );
      }

      return articleId;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.UPDATE_ARTICLE_FLAG, {
        uiFlags: isStatusUpdate
          ? { isUpdating: false, isUpdatingStatus: false }
          : { isUpdating: false },
        articleId,
      });
    }
  },

  delete: async ({ commit }, { portalSlug, articleId }) => {
    commit(types.UPDATE_ARTICLE_FLAG, {
      uiFlags: {
        isDeleting: true,
      },
      articleId,
    });
    try {
      await articlesAPI.deleteArticle({ portalSlug, articleId });
      commit(types.REMOVE_ARTICLE, articleId);
      commit(types.REMOVE_ARTICLE_ID, articleId);
      return articleId;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.UPDATE_ARTICLE_FLAG, {
        uiFlags: {
          isDeleting: false,
        },
        articleId,
      });
    }
  },

  attachImage: async (_, { file }) => {
    const { fileUrl } = await uploadFile(file);
    return fileUrl;
  },

  uploadExternalImage: async (_, { url }) => {
    const { fileUrl } = await uploadExternalImage(url);
    return fileUrl;
  },

  reorder: async (_, { portalSlug, categorySlug, reorderedGroup }) => {
    try {
      await articlesAPI.reorderArticles({
        portalSlug,
        reorderedGroup,
        categorySlug,
      });
    } catch (error) {
      throwErrorMessage(error);
    }

    return '';
  },
};
